/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 19-08-2026
 *
 * Modal-inhoud van de samenwerkdialoog (golf 3a). Vervangt `samenwerkdialoog.html` en de
 * inline controller in `cytocanvas.controller.js`.
 *
 * De dialoog stelt zijn beeld zelf samen uit de samenwerkstate en de meekijker, en werkt de rol in
 * de `SamenwerkingService` bij zodra het backend hem meldt. De stand en de drie acties (primary
 * worden, aanhaken, ontkoppelen) komen sinds golf 4c van de `ModelbeheerService`.
 */
import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {SihwconfirmService} from 'sihw-ngx';
import {BaseIslandComponent} from '../../base-island.component';
import {rolUitBackend, SamenwerkingService} from '../../../services/samenwerking.service';
import type {SamenwerkRol, SamenwerkState} from '../../../services/samenwerking.service';
import {ModelbeheerService} from '../../../app/model/modelbeheer.service';

@Component({
    selector: 'dl-samenwerkdialoog',
    standalone: true,
    templateUrl: './dl-samenwerkdialoog.component.html',
    styleUrls: ['./dl-samenwerkdialoog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, FormsModule]
})
export class DlSamenwerkdialoogComponent extends BaseIslandComponent implements OnInit, OnDestroy {

    // --- Properties ---

    /**
     * De samenwerk-sessie-state; bron van het meekijkmodel en bestemming van de rol.
     */
    protected readonly samenwerking = inject(SamenwerkingService);

    /**
     * Het modelbeheer: de samenwerkstate, de meekijker en de drie acties.
     */
    private readonly modelbeheer = inject(ModelbeheerService);

    /**
     * De modal-instantie, voor het sluiten.
     */
    private readonly bsModalRef = inject(BsModalRef);

    /**
     * De vertaalservice, voor de teksten van de ontkoppelbevestiging.
     */
    private readonly translateService = inject(TranslateService);

    /**
     * De bevestigingsdialoog uit sihw-ngx. Draait op dezelfde ngx-bootstrap-modalservice, dus
     * hij komt netjes bovenop deze dialoog te staan (zie `modal-compat.scss`).
     */
    private readonly sihwconfirm = inject(SihwconfirmService);

    /**
     * Of we op de samenwerkstate wachten.
     */
    protected laden = true;

    /**
     * De laatst opgehaalde samenwerkstate, of `null` bij een nog niet opgeslagen model.
     */
    protected stand: SamenwerkState | null = null;

    /**
     * De naam van de gebruiker van wie het gedeelde model is.
     */
    protected primaryNaam = '';

    /**
     * De naam van degene die meekijkt, of `false` als er niemand meekijkt.
     */
    protected meekijker: string | false = false;

    /**
     * De drie blokjes van de ontvangen samenwerkcode.
     */
    protected subcode: [string, string, string] = ['', '', ''];

    /**
     * De vertaalsleutel van de te tonen foutmelding, of leeg.
     */
    protected fout = '';

    /**
     * Generatie van de laatst gestarte verversing; een trager antwoord van een eerdere ronde
     * mag een nieuwere stand niet overschrijven.
     */
    private verversGeneratie = 0;

    /**
     * De bij de service geregistreerde push-ingang.
     */
    private readonly weergave = {ververs: () => void this.ververs()};

    constructor() { super(); }

    // --- Lifecycle hooks ---

    ngOnInit(): void {
        this.samenwerking.registreerWeergave(this.weergave);
        void this.ververs();
    }

    ngOnDestroy(): void {
        this.samenwerking.deregistreerWeergave(this.weergave);
    }

    // --- Public methods ---

    /**
     * De rol zoals de dialoog hem toont. Een nog niet opgeslagen model kent geen samenwerking.
     */
    get rol(): SamenwerkRol {
        return rolUitBackend(this.stand?.samenwerking);
    }

    /**
     * Of het blok met de deelnemers en de ontkoppelknop getoond wordt.
     */
    get toonDeelnemers(): boolean {
        return !this.samenwerking.meekijkmodel() && this.rol !== 'single';
    }

    /**
     * Haalt de samenwerkstate opnieuw op en stelt het beeld samen.
     */
    async ververs(): Promise<void> {
        const generatie = ++this.verversGeneratie;
        this.laden = true;
        this.cdr.markForCheck();

        const stand = await this.modelbeheer.haalSamenwerkState();
        const meekijker = stand ? await this.modelbeheer.haalMeekijker() : false;

        if (generatie !== this.verversGeneratie) {
            return; //er loopt al een nieuwere verversing
        }

        this.stand = stand;
        this.meekijker = meekijker;
        this.primaryNaam = stand ? (stand.samenwerking === 'primary' ? stand.eigenNaam : (stand.primary?.user ?? '')) : '';
        this.samenwerking.zetRolUitBackend(stand?.samenwerking);
        this.laden = false;
        this.cdr.markForCheck();
    }

    /**
     * Maakt dit model de primary van een nieuwe samenwerking. Gaat het opslaan niet door, dan
     * sluit de dialoog.
     */
    async maakPrimary(): Promise<void> {
        this.fout = '';
        this.cdr.markForCheck();
        try {
            if (!await this.modelbeheer.maakPrimary()) {
                this.bsModalRef.hide();
                return;
            }
        } catch (_e) {
            this.fout = 'SAMENWERKEN.ERROR.ALGEMEEN';
            this.cdr.markForCheck();
            return;
        }
        await this.ververs();
    }

    /**
     * Haakt aan bij de samenwerking met de ingevulde code. Bij succes is het eigen submodel geladen
     * en sluit de dialoog.
     */
    async maakSub(): Promise<void> {
        this.fout = '';
        this.cdr.markForCheck();
        const resultaat = await this.modelbeheer.haakAan(this.subcode.join('-'));
        if (resultaat === true) {
            this.bsModalRef.hide();
            return;
        }
        this.fout = `SAMENWERKEN.ERROR.${resultaat}`;
        this.cdr.markForCheck();
    }

    /**
     * Vraagt bevestiging en ontkoppelt daarna de samenwerking.
     */
    async ontkoppel(): Promise<void> {
        const bevestigd = await this.sihwconfirm.confirm(
            this.translateService.instant('SAMENWERKEN.ONTKOPPELEN'),
            this.translateService.instant(this.rol === 'primary' ? 'SAMENWERKEN.ONTKOPPELPROMPT_PRIMARY' : 'SAMENWERKEN.ONTKOPPELPROMPT_SUB'),
            this.translateService.instant('ALGEMEEN.JA'),
            this.translateService.instant('ALGEMEEN.NEE')
        );
        if (!bevestigd) {
            return;
        }
        await this.modelbeheer.ontkoppelSamenwerking();
        this.bsModalRef.hide();
    }

    /**
     * Sluit de dialoog.
     */
    sluit(): void {
        this.bsModalRef.hide();
    }
}
